import { mdiBellOutline, mdiSend } from "@mdi/js";
import { Notification } from "@timmo001/effect-system-bridge";
import { Option, Schema } from "effect";
import { html } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@timmo001/groundwork-ui/gw-segmented.ts";
import type { Segment } from "@timmo001/groundwork-ui/gw-segmented.ts";
import { selectValue } from "~/lib/select";
import { AtomController } from "~/controllers/atom-controller";
import { actionResult, registry, sendNotification } from "~/lib/atoms";

import { SbPage } from "./sb-page";

interface NotificationForm {
  readonly title: string;
  readonly message: string;
  readonly icon: string;
  readonly actionUrl: string;
  readonly sound: string;
}

type FieldKey = keyof NotificationForm;

const emptyForm: NotificationForm = {
  title: "",
  message: "",
  icon: "",
  actionUrl: "",
  sound: "",
};

interface Field {
  readonly key: FieldKey;
  readonly label: string;
  readonly placeholder: string;
  readonly hint?: string;
  readonly required?: boolean;
  readonly multiline?: boolean;
}

const fields: readonly Field[] = [
  {
    key: "title",
    label: "Title",
    placeholder: "Backup finished",
    required: true,
  },
  {
    key: "message",
    label: "Message",
    placeholder: "All files were copied",
    required: true,
    multiline: true,
  },
  {
    key: "icon",
    label: "Icon",
    placeholder: "/path/to/icon.png",
    hint: "A path to an image on that system.",
  },
  {
    key: "actionUrl",
    label: "Link",
    placeholder: "https://example.com",
    hint: "Opened when the notification is clicked.",
  },
  {
    key: "sound",
    label: "Sound",
    placeholder: "/path/to/sound.wav",
    hint: "A path to a sound file on that system.",
  },
];

const urgencyOptions: readonly Segment[] = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "critical", label: "Critical" },
];

const decodeNotification = Schema.decodeUnknownOption(Notification);

const isFieldKey = (name: string): name is FieldKey =>
  fields.some((field) => field.key === name);

/** Send a desktop notification to this system. */
@customElement("page-notifications")
export class PageNotifications extends SbPage {
  readonly #send = new AtomController(this, () => sendNotification);

  @state() private form: NotificationForm = emptyForm;

  /** Sent only when it isn't normal, which is the default. */
  @state() private urgency = "normal";

  protected readonly pageTitle = "Notifications";

  /** Fields are named after their key in the form. */
  #onInput = (event: InputEvent) => {
    const target = event.target;

    if (
      (target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement) &&
      isFieldKey(target.name)
    ) {
      this.form = { ...this.form, [target.name]: target.value };
    }
  };

  #clear = () => {
    this.form = emptyForm;
    this.urgency = "normal";
  };

  #onUrgency = (event: Event) => {
    this.urgency = selectValue(event);
  };

  #onSubmit = (event: SubmitEvent) => {
    event.preventDefault();

    // Blank optional fields are left out, and the schema checks the rest
    const filled = fields.flatMap(({ key }) => {
      const value = this.form[key].trim();

      return value === "" ? [] : [[key, value] as const];
    });

    const urgency =
      this.urgency === "normal" ? [] : [["urgency", this.urgency] as const];

    Option.match(
      decodeNotification(Object.fromEntries([...filled, ...urgency])),
      {
        onNone: () => undefined,
        onSome: (notification) => registry.set(sendNotification, notification),
      },
    );
  };

  #renderField(field: Field, sending: boolean) {
    return html`
      <gw-input
        label=${field.label}
        name=${field.key}
        placeholder=${field.placeholder}
        hint=${field.hint ?? ""}
        ?required=${field.required === true}
        ?optional=${field.required !== true}
        ?multiline=${field.multiline === true}
        ?monospace=${field.key === "icon" || field.key === "sound"}
        ?disabled=${sending}
        .value=${this.form[field.key]}
        @input=${this.#onInput}
      ></gw-input>
    `;
  }

  #renderForm() {
    const sending = this.#send.value.waiting;

    const ready =
      this.form.title.trim() !== "" && this.form.message.trim() !== "";

    const inputs = fields.map((field) => this.#renderField(field, sending));

    return html`
      <form @submit=${this.#onSubmit}>
        <gw-card>
          <div class="stack">
            ${inputs}
            <gw-setting-row
              heading="Urgency"
              description="Critical notifications stay until they're dismissed. Linux only."
            >
              <gw-segmented
                label="Urgency"
                size="small"
                .options=${urgencyOptions}
                .value=${this.urgency}
                @change=${this.#onUrgency}
              ></gw-segmented>
            </gw-setting-row>
          </div>
          <div slot="footer" class="row" style="justify-content: flex-end">
            <gw-button
              variant="neutral"
              appearance="plain"
              ?disabled=${sending}
              @click=${this.#clear}
              >Clear</gw-button
            >
            <gw-button
              type="submit"
              icon=${mdiSend}
              ?loading=${sending}
              ?disabled=${!ready}
              >Send</gw-button
            >
          </div>
        </gw-card>
      </form>
    `;
  }

  override render() {
    return html`
      <div class="page narrow">
        ${this.renderHeader({
          heading: "Notifications",
          description:
            "Send a desktop notification, shown by the system's own notifications",
          icon: mdiBellOutline,
        })}
        ${this.renderResult(
          actionResult(this.#send.value, "The notification was sent."),
        )}
        ${this.gate(
          () => this.#renderForm(),
          html`<gw-skeleton
            style="min-height: 30rem; border-radius: var(--gw-radius-l)"
          ></gw-skeleton>`,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-notifications": PageNotifications;
  }
}
